import { ChangeDetectionStrategy, ChangeDetectorRef, Component, DoCheck, EventEmitter, Input, Output } from '@angular/core';
import ArticleGroup from '../../../smoothr-web-app-core/models/ArticleGroup';
import { OrderListAction } from '../../enums/OrderListAction';
import ArticleOption from '../../../smoothr-web-app-core/models/ArticleOption';
import { DisplayIdentifier } from '../../../smoothr-web-app-core/enums/DisplayIdentifier';
import { OrderUtils } from '../../../smoothr-web-app-core/utils/order-utils';
import Article from '../../../smoothr-web-app-core/models/Article';
import { ValidationUtils } from '../../../smoothr-web-app-core/utils/validation-utils';
import Order from '../../../smoothr-web-app-core/models/Order';
import { getPrice, numberD, numberToCurrency } from '../../../smoothr-web-app-core/utils/utils';
import OptionGroup from 'src/smoothr-web-app-core/models/OptionGroup';
import Venue from 'src/smoothr-web-app-core/models/Venue';
import { environment } from '../../../environments/environment';
import ArticleCategory from '../../../smoothr-web-app-core/models/ArticleCategory';
import { SmoothrCNCDiscount } from '../../../smoothr-web-app-core/models/CNCDiscount';
import { RepositoryService } from '../../../smoothr-web-app-core/services/repository/repository.service';
import { consumerDisplayDiscounts } from '../../shared';

@Component({
	selector: 'app-order-list',
	templateUrl: './order-list.component.html',
	styleUrls: ['order-list.component.scss'],
	standalone: false,
	changeDetection: ChangeDetectionStrategy.OnPush,
})
export class OrderListComponent implements DoCheck {
	@Output() action = new EventEmitter<OrderListAction>();
	@Input() editable = true;
	@Input() hideAllActions = false;
	OrderUtils = OrderUtils;
	isSomeOptionFullPrice = false;
	needsConfiguration = false;
	articleGroupPrice = 0;
	numberToCurrency = numberToCurrency;
	groupedOptions: { listOfOptionArticles: ArticleOption[]; group: OptionGroup }[] = [];
	@Input() venue: Venue;
	@Input() set promoCategories(value: ArticleCategory[]) {
		this._promoCategories = value;
		this.reloadArticle();
	}
	private _promoCategories: ArticleCategory[] = [];
	private prevPromoState = false;
	private prevQuantity = 1;

	constructor(
		private cd: ChangeDetectorRef,
		private repositoryService: RepositoryService
	) {}

	get discountBadges(): SmoothrCNCDiscount[] {
		return consumerDisplayDiscounts(this.repositoryService, this.articleGroup?.article);
	}

	private _articleGroup: ArticleGroup;

	get articleGroup(): ArticleGroup {
		return this._articleGroup;
	}

	@Input()
	set articleGroup(value: ArticleGroup) {
		this._articleGroup = this.mapGroup(value);
		this.reloadArticle();
	}

	private _order: Order;

	get order(): Order {
		return this._order;
	}

	@Input()
	set order(value: Order) {
		this._order = value;
		this._articleGroup = this.mapGroup(this.articleGroup);

		this.reloadArticle();
	}

	mapGroup(articleGroup: ArticleGroup) {
		if (!this.venue || !this.order) {
			return articleGroup;
		}
		if (this.venue) {
			const foundArticle = this.venue.articleCategories
				.map(it => {
					if (it.visible) {
						const foundArticle = it.articles.find(ar => ar._id === articleGroup.article._id);

						return foundArticle ?? null;
					} else {
						return null;
					}
				})
				.filter(it => !!it);
			if (foundArticle.length === 1) {
				const test = JSON.parse(JSON.stringify(articleGroup)) as ArticleGroup;
				test.article = JSON.parse(JSON.stringify(foundArticle[0]));

				return test;
			}
			return articleGroup;
		}
		return articleGroup;
	}

	edit() {
		if (!this.isEditable()) {
			return;
		}
		this.action.emit(OrderListAction.edit);
	}

	isEditable(): boolean {
		return this.editable;
	}

	delete() {
		if (!this.isEditable()) {
			return;
		}
		this.action.emit(OrderListAction.delete);
	}

	async note() {
		if (!this.editable) {
			return;
		}
		this.action.emit(OrderListAction.note);
	}

	add() {
		if (!this.isEditable()) {
			return;
		}
		this.action.emit(OrderListAction.add);
	}

	remove() {
		if (!this.isEditable()) {
			return;
		}
		this.action.emit(OrderListAction.remove);
	}

	isOptionFullPrice(articleOption: ArticleOption): boolean {
		const group = this.articleGroup.article.groups.find(grp => grp._id === articleOption.group);
		return group && group.displayIdentifiers && group.displayIdentifiers.findIndex(di => DisplayIdentifier.fullPrice === di) >= 0;
	}

	hiddenInCart(article: Article): boolean {
		return article.tags && article.tags.find(tag => tag.identifier === 'hide_cart') !== undefined;
	}

	reloadArticle() {
		this.needsConfiguration = false;
		this.isSomeOptionFullPrice = false;
		this.articleGroupPrice = 0;
		this.prevPromoState = false;
		this.prevQuantity = 1;
		if (!this.articleGroup || !this.order || !this.order.preorder) {
			this.articleGroupPrice = 0;
			this.groupedOptions = [];
			return;
		}
		this.prevQuantity = this.articleGroup.quantity;
		this.prevPromoState = this.articleGroup.isPromo;
		this.needsConfiguration = !ValidationUtils.areGroupsValid(this.articleGroup, this.articleGroup.article.groups);
		for (const articleOption of this.articleGroup.groups) {
			if (this.isOptionFullPrice(articleOption)) {
				this.isSomeOptionFullPrice = true;
			}
		}
		this.articleGroupPrice = OrderUtils.totalPrice(this.articleGroup, this.order.type, this.order.preorder.type);
		if (OrderUtils.isBogoOrFreeArticlePromo(this.order) && this.articleGroup.isPromo) {
			this.articleGroupPrice -= OrderUtils.bogoPrice(this.articleGroup, this.order.type, this.order.preorder.type);
		}
		// Cache the grouped options
		this.groupedOptions = this.calculateGroupedOptions();
	}

	ngDoCheck(): void {
		if (this.prevPromoState !== this.articleGroup.isPromo) {
			this.reloadArticle();
			this.cd.markForCheck();
		}
		if (this.prevQuantity !== this.articleGroup.quantity) {
			this.reloadArticle();
			this.cd.markForCheck();
		}
	}

	priceOfOption(articleOption: ArticleOption): number {
		if (articleOption.group === '6615e6b444b15eee9c5e6738' || articleOption.group === '6615e6c344b15eee9c5e6e7d') {
			return 0;
		}
		if (articleOption && articleOption?.article?.name?.de?.includes('Pfand')) {
			return 0;
		}
		if (
			articleOption?.article?.displayPriceByType?.preorder &&
			numberD(articleOption?.article?.displayPriceByType?.preorder?.takeAway) === 0
		) {
			return 0;
		}
		let price = getPrice(articleOption.article, this.order.type, this.order.preorder?.type);
		// if (this.isOptionFullPrice(articleOption)) {
		// 	price = price + this.articleGroupPrice;
		// 	if (OrderUtils.isBogoOrFreeArticlePromo(this.order) && this.articleGroup.isPromo) {
		// 		return 0;
		// 	}
		// }
		if (this.articleGroup) {
			const findGroup = this.articleGroup.article.groups.find(it => it._id === articleOption.group);
			if (findGroup) {
				if (findGroup.tags.find(it => it.identifier === 'half_pizza')) {
					return 0;
				}
			}
		}

		return price * articleOption.quantity;
	}

	trackByGroupId(index: number, item: { listOfOptionArticles: ArticleOption[]; group: OptionGroup }): string {
		return item.group?._id || index.toString();
	}

	trackByOptionId(index: number, item: ArticleOption): string {
		return item.article?._id + '_' + item.group || index.toString();
	}

	private calculateGroupedOptions(): { listOfOptionArticles: ArticleOption[]; group: OptionGroup }[] {
		if (!this.articleGroup || !this.articleGroup.article || !this.articleGroup.article.groups) {
			return [];
		}

		const groupsById: Record<string, OptionGroup> = {};
		for (const group of this.articleGroup.article.groups) {
			groupsById[group._id] = group;
		}

		const result: Record<string, { listOfOptionArticles: ArticleOption[]; group: OptionGroup }> = {};

		for (const option of this.articleGroup.groups) {
			if (!result[option.group]) {
				result[option.group] = {
					listOfOptionArticles: [],
					group: groupsById[option.group] || null,
				};
			}
			result[option.group].listOfOptionArticles.push(option);
		}
		return Object.values(result);
	}
}
